import { z } from "zod";

import { i18n } from "~/lib/i18n";
import { moneyInputSchema } from "~/shared/services/money";
import { VISIT_PERIODS } from "~/shared/services/visit-series";

import type { RouterOutputs } from "~/lib/trpc";

const rewardTypeSchema = z.enum(["free_dish", "percentage_discount", "special_price"]);
const customerSortSchema = z.enum([
  "email",
  "stampsBalance",
  "totalVisits",
  "firstVisitAt",
  "lastVisitAt",
  "rewardsRedeemed",
]);
const sortDirectionSchema = z.enum(["asc", "desc"]);
const visitsPeriodSchema = z.enum(VISIT_PERIODS);
const searchBoolean = z.union([z.boolean(), z.stringbool()]).catch(false);
export const loyaltyInsightsSearchSchema = z.object({
  search: z.string().trim().catch(""),
  page: z.coerce.number().int().min(1).catch(1),
  sortBy: customerSortSchema.catch("lastVisitAt"),
  sortDir: sortDirectionSchema.catch("desc"),
  inactive: searchBoolean,
  period: visitsPeriodSchema.catch("30d"),
});
const optionalMoneySchema = moneyInputSchema.or(z.literal(""));
const costSchema = z
  .string()
  .trim()
  .regex(/^[1-9]\d*$/, i18n.t("loyalty___El coste debe ser un número entero de sellos"));
const percentageSchema = z
  .string()
  .trim()
  .regex(/^(?:100|\d{1,2}|)$/, i18n.t("loyalty___Indica un porcentaje entre 0 y 100"));
export const rewardFormSchema = z
  .object({
    rewardId: z.string().nullable(),
    name: z
      .string()
      .trim()
      .min(1, { message: i18n.t("loyalty___Escribe un nombre para el premio") })
      .max(200),
    description: z.string().trim().max(2000),
    cost: costSchema,
    type: rewardTypeSchema,
    percentage: percentageSchema,
    specialPrice: optionalMoneySchema,
    freeDishId: z.string(),
    isActive: z.boolean(),
  })
  .superRefine((reward, context) => {
    if (reward.type === "free_dish" && !reward.freeDishId) {
      context.addIssue({ code: "custom", path: ["freeDishId"], message: i18n.t("loyalty___Selecciona un plato") });
    }
    if (reward.type === "percentage_discount" && reward.percentage === "") {
      context.addIssue({ code: "custom", path: ["percentage"], message: i18n.t("loyalty___Indica un porcentaje") });
    }
    if (reward.type === "special_price") {
      if (!reward.freeDishId) {
        context.addIssue({ code: "custom", path: ["freeDishId"], message: i18n.t("loyalty___Selecciona un plato") });
      }
      if (reward.specialPrice === "") {
        context.addIssue({
          code: "custom",
          path: ["specialPrice"],
          message: i18n.t("loyalty___Indica un precio especial"),
        });
      }
    }
  });
export const loyaltyProgramFormSchema = z.object({
  isActive: z.boolean(),
  averageTicket: optionalMoneySchema,
  stampRequestLimit: z
    .string()
    .trim()
    .regex(/^(?:10|[1-9])$/, i18n.t("loyalty___Indica un número entre 1 y 10")),
  rewards: z.array(rewardFormSchema),
});
export type CustomerSort = z.infer<typeof customerSortSchema>;
export type LoyaltyInsightsSearch = z.infer<typeof loyaltyInsightsSearchSchema>;
export type LoyaltyProgramFormValues = z.infer<typeof loyaltyProgramFormSchema>;
export type RewardFormValues = z.infer<typeof rewardFormSchema>;
export type RewardType = z.infer<typeof rewardTypeSchema>;
export type SortDirection = z.infer<typeof sortDirectionSchema>;
export type VisitsPeriod = (typeof VISIT_PERIODS)[number];
export type LoyaltyProgramResponse = RouterOutputs["admin"]["loyalty"]["getProgram"];
export type PendingRedemption = RouterOutputs["admin"]["loyalty"]["pendingRedemptions"][number];
export type PendingStampRequest = RouterOutputs["admin"]["loyalty"]["pendingStampRequests"][number];
export type HandledStampRequest = RouterOutputs["admin"]["loyalty"]["handledStampRequests"][number];
export type LoyaltyCustomer = RouterOutputs["admin"]["loyalty"]["insights"]["customers"]["rows"][number];
export type LoyaltyVisitsPoint = RouterOutputs["admin"]["loyalty"]["insights"]["visitsChart"][number];
export type DishGroup = { label: string; options: Array<{ id: string; label: string }> };
